import React from 'react';
import { cn } from '@/lib/utils';

// --- TYPE DEFINITIONS ---

interface SegmentColor {
  bg: string;
  text: string;
  label?: string;
}

interface Segment {
  id: string;
  label: string;
  value: number;
  color?: SegmentColor;
}

interface ProgressSegmentProps {
  segment: Segment;
  widthPercentage: number;
  showLabel?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  onClick?: (segment: Segment) => void;
  className?: string;
}

interface ProgressIndicatorBarProps extends React.HTMLAttributes<HTMLDivElement> {
  segments?: Segment[];
  showLabels?: boolean;
  showValues?: boolean;
  normalize?: boolean;
  height?: string;
  onSegmentClick?: (segment: Segment) => void;
}

// --- CONSTANTS ---

const DEFAULT_HEIGHT = 'h-5';
const DEFAULT_BORDER_RADIUS = 'rounded-full';
const MIN_SEGMENT_WIDTH = 5; // Minimum width percentage for visibility

const SEGMENT_COLORS: Record<string, SegmentColor> = {
  CURRENT: { bg: 'bg-emerald-400', text: 'text-emerald-900' },
  THIRTY_DAYS: { bg: 'bg-orange-300', text: 'text-orange-900' },
  SIXTY_NINETY_DAYS: { bg: 'bg-rose-300', text: 'text-rose-900' },
};

const DEFAULT_SEGMENTS: Segment[] = [
  { id: 'current', label: 'Current', value: 40, color: SEGMENT_COLORS.CURRENT },
  { id: 'thirty_days', label: '30 days', value: 35, color: SEGMENT_COLORS.THIRTY_DAYS },
  { id: 'sixty_ninety_days', label: '60 - 90 days', value: 25, color: SEGMENT_COLORS.SIXTY_NINETY_DAYS },
];

// --- INTERNAL SEGMENT COMPONENT ---

const ProgressSegment: React.FC<ProgressSegmentProps> = ({
  segment,
  widthPercentage,
  showLabel = true,
  isFirst = false,
  isLast = false,
  onClick,
  className = '',
}) => {
  const handleClick = () => {
    onClick?.(segment);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      style={{ width: `${widthPercentage}%` }}
      className={cn(
        'flex items-center justify-center transition-all duration-300 ease-in-out',
        'text-xs font-medium text-white',
        segment.color?.bg || 'bg-gray-400',
        isFirst && 'rounded-l-full',
        isLast && 'rounded-r-full',
        onClick && 'cursor-pointer hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2',
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={onClick ? 0 : -1}
      role="button"
      aria-label={`Segment: ${segment.label}, Value: ${segment.value}`}
    >
      {showLabel && <span className="truncate px-2">{segment.label}</span>}
    </div>
  );
};

ProgressSegment.displayName = 'ProgressSegment';

// --- MAIN COMPONENT ---

const ProgressIndicatorBar: React.FC<ProgressIndicatorBarProps> = ({
  segments = DEFAULT_SEGMENTS,
  showLabels = true,
  showValues = false,
  normalize = true,
  height = DEFAULT_HEIGHT,
  onSegmentClick,
  className = '',
  ...props
}) => {
  const validSegments = segments.filter(s => s && typeof s.value === 'number' && s.value >= 0);

  const totalValue = validSegments.reduce((sum, segment) => sum + segment.value, 0);

  if (totalValue === 0) {
    return null; // Don't render if there's nothing to show
  }

  const segmentsWithWidths = validSegments.map(segment => {
    const widthPercentage = normalize ? (segment.value / totalValue) * 100 : segment.value;
    return {
      ...segment,
      widthPercentage: Math.max(widthPercentage, widthPercentage > 0 ? MIN_SEGMENT_WIDTH : 0),
    };
  });

  return (
    <div className={cn('w-full', className)} {...props}>
        <div
            className={cn('flex overflow-hidden w-full', height, DEFAULT_BORDER_RADIUS)}
            role="progressbar"
            aria-label="Progress indicator with multiple segments"
            aria-valuemax={100}
            aria-valuemin={0}
        >
            {segmentsWithWidths.map((segment, index) => (
                <ProgressSegment
                    key={segment.id}
                    segment={segment}
                    widthPercentage={segment.widthPercentage}
                    showLabel={showLabels}
                    isFirst={index === 0}
                    isLast={index === segmentsWithWidths.length - 1}
                    onClick={onSegmentClick}
                />
            ))}
        </div>
        {showValues && (
            <div className="flex justify-between mt-2 px-1 text-xs text-gray-600">
                {segmentsWithWidths.map(segment => (
                    <div key={`${segment.id}-value`} style={{ width: `${segment.widthPercentage}%`}} className="text-center">
                        <span>{segment.value}%</span>
                    </div>
                ))}
            </div>
        )}
    </div>
  );
};

ProgressIndicatorBar.displayName = 'ProgressIndicatorBar';

export { ProgressIndicatorBar, DEFAULT_SEGMENTS, SEGMENT_COLORS };
export type { ProgressIndicatorBarProps, Segment as ProgressIndicatorBarSegment };
